import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import {
  Bell,
  Bot,
  Briefcase,
  Building2,
  ClipboardList,
  Coins,
  CreditCard,
  Database,
  FileText,
  Handshake,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageCircle,
  Palette,
  PanelLeft,
  PanelLeftClose,
  Send,
  ShieldAlert,
  TrendingUp,
  User,
  Users,
  Webhook,
} from 'lucide-react';
import { useAdminAlertsBadge } from '../../hooks/admin/useAdminAlerts';
import ThemeToggle from '../ThemeToggle';
import SandboxTryoutButton from '../SandboxTryoutButton';
import KolMyGlassLogo from '../KolMyGlassLogo';
import AppearanceSettings from '../AppearanceSettings';
import { useDesktopSidebarHidden } from '../../hooks/useDesktopSidebarHidden';
import { portalShellProps } from '../../lib/appearance';
import { portalContentClassName } from '../../lib/portalContent';
import {
  PortalNavGroups,
  flatNavFromGroups,
  navLinkBase,
  navLinkInactive,
  isNavItemActive,
  type NavGroup,
} from './PortalNav';
import { PortalMobileTopBar } from './PortalMobileTopBar';

export default function AdminLayout() {
  const { logout, user } = useAuthStore();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const { isDesktopSidebarHidden, setIsDesktopSidebarHidden } = useDesktopSidebarHidden();
  const { count: unackedAlerts } = useAdminAlertsBadge();

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, user?.id]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        label: 'Overview',
        items: [{ path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', exact: true }],
      },
      {
        label: 'Management',
        items: [
          { path: '/admin/users', icon: Users, label: 'Users' },
          { path: '/admin/kols', icon: Users, label: 'KOLs' },
          { path: '/admin/brands', icon: Building2, label: 'Brands' },
          { path: '/admin/agencies', icon: Handshake, label: 'Agencies' },
          { path: '/admin/campaigns', icon: Briefcase, label: 'Campaigns' },
          { path: '/admin/contracts', icon: FileText, label: 'Contracts' },
          { path: '/admin/payments', icon: CreditCard, label: 'Payments' },
        ],
      },
      {
        label: 'System',
        items: [
          { path: '/admin/ai-credits', icon: Coins, label: 'AI credits' },
          { path: '/admin/messaging', icon: MessageCircle, label: 'Messaging' },
          { path: '/admin/messaging-templates', icon: Mail, label: 'Messaging templates' },
          { path: '/admin/invitations', icon: Send, label: 'Invitations' },
          { path: '/admin/messaging-log', icon: ClipboardList, label: 'Messaging log' },
          { path: '/admin/plunk-webhooks', icon: Webhook, label: 'Plunk Email' },
        ],
      },
      {
        label: 'Growth & defense',
        items: [
          { path: '/admin/chatbot-log', icon: Bot, label: 'Chatbot log' },
          {
            path: '/admin/security',
            icon: ShieldAlert,
            label: 'Security',
            badge: unackedAlerts > 0 ? unackedAlerts : undefined,
          },
          { path: '/admin/growth', icon: TrendingUp, label: 'Growth' },
          { path: '/admin/data-sources', icon: Database, label: 'Data sources' },
        ],
      },
    ],
    [unackedAlerts]
  );

  const allNavItems = flatNavFromGroups(navGroups);
  const pageTitle =
    allNavItems.find((item) => isNavItemActive(location.pathname, item))?.label || 'Admin';

  const shell = portalShellProps('admin');

  return (
    <div
      className="theme-admin glass-mesh-bg flex h-screen text-glass-text font-sans"
      {...shell}
    >
      <PortalMobileTopBar
        menuOpen={isMobileMenuOpen}
        onToggleMenu={() => setIsMobileMenuOpen((open) => !open)}
        notificationsTo="/admin/notifications"
        unreadCount={unreadCount}
      />

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 mt-12 bg-[var(--glass-scrim)] backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden
        />
      )}

      <div
        className={`
          fixed md:static inset-y-0 left-0 z-50
          w-[264px] flex flex-col
          bg-glass-1 backdrop-blur-[28px] border-r border-glass
          transform transition-transform duration-300 ease-in-out
          md:translate-x-0
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isDesktopSidebarHidden ? 'md:hidden' : ''}
          mt-12 md:mt-0
        `}
      >
        <div className="p-5 border-b border-glass">
          <Link
            to="/"
            className="inline-flex shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 rounded-md"
          >
            <KolMyGlassLogo variant="nav" />
          </Link>
          <p className="text-xs uppercase tracking-[0.05em] text-glass-text-2 mt-3">Platform Admin</p>
        </div>

        <PortalNavGroups groups={navGroups} />

        <div className="p-3 border-t border-glass space-y-1">
          <div className="flex items-center gap-2 px-1 pb-2 md:hidden" aria-label="Portal utilities">
            <SandboxTryoutButton className="!h-11 !w-11" />
            <ThemeToggle className="!h-11 !w-11" />
          </div>
          <button
            type="button"
            onClick={() => setAppearanceOpen((v) => !v)}
            className={`${navLinkBase} ${navLinkInactive} w-full text-left`}
          >
            <Palette className="w-5 h-5" />
            Appearance
          </button>
          {appearanceOpen ? (
            <div className="px-2 pb-2">
              <AppearanceSettings showAgencyAccent={false} compact />
            </div>
          ) : null}
          <Link to="/admin/profile" className={`${navLinkBase} ${navLinkInactive}`}>
            <User className="w-5 h-5" />
            Profile
          </Link>
          <button type="button" onClick={logout} className={`${navLinkBase} ${navLinkInactive} w-full text-left`}>
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      <div className="mt-12 flex flex-1 flex-col overflow-hidden md:mt-0">
        <div className="hidden md:flex h-16 bg-glass-1 backdrop-blur-[28px] border-b border-glass items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsDesktopSidebarHidden(!isDesktopSidebarHidden)}
              className="p-2 text-glass-text-2 hover:text-glass-text hover:bg-glass-3 rounded-lg transition-colors"
              aria-label={isDesktopSidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
              title={isDesktopSidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
            >
              {isDesktopSidebarHidden ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            <h2 className="page-title text-[1.15rem]">{pageTitle}</h2>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-glass-2 border border-glass">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[0.7rem] font-bold text-white"
                  style={{
                    background:
                      'linear-gradient(135deg, rgb(var(--accent-rgb) / 0.85), rgb(var(--accent2-rgb) / 0.65))',
                  }}
                >
                  {(user.fullName || user.email || '?').slice(0, 1).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-glass-text max-w-[140px] truncate">
                  {user.fullName || user.email}
                </span>
              </div>
            )}
            <SandboxTryoutButton />
            <ThemeToggle />
            <Link
              to="/admin/notifications"
              className="relative p-2 text-glass-text-2 hover:text-glass-text hover:bg-glass-3 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-accent2 text-white text-[0.6rem] flex items-center justify-center rounded-full shadow-[0_0_10px_rgb(var(--accent-rgb)/0.6)]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        <div className="flex-1 overflow-auto glass-scroll">
          <div className={portalContentClassName(location.pathname)}>
            <Outlet />
          </div>
        </div>
      </div>
      <div data-portal-overlay-root />
    </div>
  );
}
